import { Fragment, useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import PermissionWrapper from "../PermissionWrapper";

const InventoryPurchaseOrderList = () => {
 const navigate = useNavigate();
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [pos, setPos] = useState([]);
 const [vendors, setVendors] = useState([]);
 const [warehouses, setWarehouses] = useState([]);
 const [loading, setLoading] = useState(false);
 const [filters, setFilters] = usePersistentFilters("inventory_pos_filters", { search: "", status: "", vendor: "", warehouse: "", page: 1, limit: 10 });
 const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });

 useEffect(() => {
 loadLookups();
 }, []);

 useEffect(() => {
 fetchPOs();
 }, [filters.page, filters.limit, filters.status, filters.vendor, filters.warehouse]);

 const loadLookups = async () => {
 try {
 const [vendorsRes, warehousesRes] = await Promise.all([
 api.get("/vendors?limit=500"),
 api.get("/warehouses?limit=500"),
 ]);
 if (vendorsRes.data.success) setVendors(vendorsRes.data.data || []);
 if (warehousesRes.data.success) setWarehouses(warehousesRes.data.data || []);
 } catch (error) {
 swal("Error", "Failed to load filter data", "error");
 }
 };

 const fetchPOs = async () => {
 try {
 setLoading(true);
 const queryParams = new URLSearchParams();
 if (filters.search) queryParams.append("search", filters.search);
 if (filters.status) queryParams.append("status", filters.status);
 if (filters.vendor) queryParams.append("vendor", filters.vendor);
 if (filters.warehouse) queryParams.append("warehouse", filters.warehouse);
 queryParams.append("page", filters.page);
 queryParams.append("limit", filters.limit);

 const res = await api.get(`/inventory-purchase-orders?${queryParams.toString()}`);
 if (res.data.success) {
 setPos(res.data.data || []);
 setPagination(res.data.pagination || pagination);
 }
 } catch (error) {
 swal("Error", "Failed to load inventory purchase orders", "error");
 } finally {
 setLoading(false);
 }
 };

 const handleDelete = async (id) => {
 const ok = await swal({
 title: "Delete inventory PO?",
 text: "This record will be removed permanently.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });
 if (!ok) return;

 try {
 const res = await api.delete(`/inventory-purchase-orders/${id}`);
 if (res.data.success) {
 swal("Deleted", "Inventory PO deleted successfully", "success");
 fetchPOs();
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to delete inventory PO", "error");
 }
 };

 const statusBadge = (status) => {
 switch (status) {
 case "Received":
 return "success";
 case "Partially Received":
 return "warning";
 case "Cancelled":
 return "danger";
 case "Issued":
 return "info";
 default:
 return "secondary";
 }
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Inventory Purchase Orders" motherMenu="Inventory" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Inventory Purchase Orders</Card.Title>
 <div className="d-flex gap-2">
 <Button variant="secondary" as={Link} to="/inventory-dashboard">Back</Button>
 <PermissionWrapper user={user} required={["create:inventory"]}>
 <Button as={Link} to="/inventory-purchase-orders/add">+ Add Inventory PO</Button>
 </PermissionWrapper>
 </div>
 </Card.Header>
 <Card.Body>
 <Row className="g-2 mb-3">
 <Col md={4}>
 <Form.Control
 placeholder="Search PO"
 value={filters.search}
 onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
 onKeyDown={(e) => e.key === "Enter" && fetchPOs()}
 />
 </Col>
 <Col md={2}>
 <Form.Select value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value, page: 1 }))}>
 <option value="">All Status</option>
 <option value="Draft">Draft</option>
 <option value="Issued">Issued</option>
 <option value="Partially Received">Partially Received</option>
 <option value="Received">Received</option>
 <option value="Cancelled">Cancelled</option>
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Select value={filters.vendor} onChange={(e) => setFilters((prev) => ({ ...prev, vendor: e.target.value, page: 1 }))}>
 <option value="">All Vendors</option>
 {vendors.map((vendor) => <option key={vendor._id} value={vendor._id}>{vendor.name}</option>)}
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Select value={filters.warehouse} onChange={(e) => setFilters((prev) => ({ ...prev, warehouse: e.target.value, page: 1 }))}>
 <option value="">All Warehouses</option>
 {warehouses.map((warehouse) => (
 <option key={warehouse._id} value={warehouse._id}>
 {warehouse.warehouseName}
 </option>
 ))}
 </Form.Select>
 </Col>
 </Row>

 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th style={{ textAlign: "center" }}>PO No</th>
 <th style={{ textAlign: "center" }}>Vendor</th>
 <th style={{ textAlign: "center" }}>Warehouse</th>
 <th style={{ textAlign: "center" }}>Items</th>
 <th style={{ textAlign: "center" }}>Total</th>
 <th style={{ textAlign: "center" }}>Status</th>
 <th style={{ textAlign: "center" }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {!loading && pos.map((po) => (
 <tr key={po._id}>
 <td style={{ textAlign: "center" }}>{po.inventoryPoNumber}</td>
 <td style={{ textAlign: "center" }}>{po.vendor?.name || "-"}</td>
 <td style={{ textAlign: "center" }}>{po.warehouse?.warehouseName || "-"}</td>
 <td style={{ textAlign: "center" }}>{po.items?.length || 0}</td>
 <td style={{ textAlign: "center" }}>Rs. {Number(po.totalAmount || 0).toFixed(2)}</td>
 <td style={{ textAlign: "center" }}><Badge bg={statusBadge(po.status)}>{po.status}</Badge></td>
 <td>
 <div className="d-flex justify-content-center">
 <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => navigate(`/inventory-purchase-orders/view/${po._id}`)}>
 View
 </Button>
 <PermissionWrapper user={user} required={["edit:inventory"]}>
 <Button size="sm" variant="outline-primary" className="me-2" onClick={() => navigate(`/inventory-purchase-orders/edit/${po._id}`)}>
 Edit
 </Button>
 {po.status !== "Received" && (
 <Button size="sm" variant="outline-danger" onClick={() => handleDelete(po._id)}>
 Delete
 </Button>
 )}
 </PermissionWrapper>
 </div>
 </td>
 </tr>
 ))}
 {!loading && pos.length === 0 && (
 <tr>
 <td colSpan="7" className="text-center text-muted py-4">No inventory purchase orders found</td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>

 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={pos.length}
 limit={filters.limit}
 onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
 />
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default InventoryPurchaseOrderList;
